import { LuIcon, Menu, type MenuClickTriggerProps, type MenuRootProps, type MenuVariantProps } from "@overmap-ai/blocks"
import { type AssetType, nameModelSortFn, type Stored } from "@overmap-ai/common"
import { useSort, useTextFilter } from "@overmap-ai/hooks"
import { memo, type ReactElement } from "react"

import { assetTypeFilterCallback } from "../../filtering/utils"
import { modelIconMapping } from "../../mappings/icons"
import {
	AssetTypeMultiSelectGroup,
	type AssetTypeMultiSelectGroupProps,
} from "../MenuMultiSelectGroups/AssetTypeMultiSelectGroup"

interface AssetTypeMultiSelectProps
	extends
		MenuRootProps,
		AssetTypeMultiSelectGroupProps,
		MenuVariantProps,
		Omit<MenuClickTriggerProps, "children" | "ref"> {
	children: ReactElement
}

export const AssetTypeMultiSelect = memo((props: AssetTypeMultiSelectProps) => {
	const { children, values, onValuesChange, assetTypes, disabled, variant, size, accentColor, ...rest } = props

	const sortedAssetTypes = useSort(assetTypes, nameModelSortFn)

	const [filteredAssetTypes, filterValue, setFilterValue] = useTextFilter<Stored<AssetType>>(
		sortedAssetTypes,
		assetTypeFilterCallback,
	)

	const isEmpty = filteredAssetTypes.length === 0

	return (
		<Menu.Root {...rest}>
			<Menu.ClickTrigger disabled={disabled}>{children}</Menu.ClickTrigger>
			<Menu.Content variant={variant} size={size} accentColor={accentColor}>
				<Menu.InputRoot>
					<Menu.InputSlot>
						<LuIcon icon={modelIconMapping.search} />
					</Menu.InputSlot>
					<Menu.InputField placeholder="Search" value={filterValue} onValueChange={setFilterValue} />
				</Menu.InputRoot>
				{!isEmpty ? <Menu.Separator /> : null}
				<Menu.Scroll>
					{!isEmpty ? (
						<AssetTypeMultiSelectGroup
							values={values}
							assetTypes={filteredAssetTypes}
							onValuesChange={onValuesChange}
						/>
					) : null}
				</Menu.Scroll>
			</Menu.Content>
		</Menu.Root>
	)
})
AssetTypeMultiSelect.displayName = "AssetTypeMultiSelect"
